import { useEffect, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useApp } from "../../../app/context/useApp";
import { getClientById } from "../../../core/services/client.service";
import { getFileByProject } from "../../../core/services/project.service";
import {
  getClientDisplayName,
  getClientId,
} from "../../../core/utils/clientDisplay";
import {
  getProjectId,
  getProjectName,
  getProjectSourcesCount,
} from "../../../layout/Header/utils/projectUtils";
import { extractProjectFilesList } from "../../../layout/Header/components/CreateProjectModal/shared";
import {
  dispatchOpenKbUpload,
  KB_UPLOADED_EVENT,
} from "../../../layout/kbUploadEvents";
import { mobileNavMediaQuery } from "../../../layout/layoutMetrics";

const UPLOAD_LINK_COLOR = "#D4A017";
const MESSAGE_COLOR = "rgba(255,255,255,0.38)";

export default function NoKnowledgeBankHint() {
  const { selectedClient, selectedProject } = useApp();
  const [message, setMessage] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const clientId = getClientId(selectedClient);
  const projectId = getProjectId(selectedProject);

  useEffect(() => {
    const onKbUploaded = () => setRefreshKey((key) => key + 1);
    window.addEventListener(KB_UPLOADED_EVENT, onKbUploaded);
    return () => window.removeEventListener(KB_UPLOADED_EVENT, onKbUploaded);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const resolveMessage = async () => {
      if (!clientId && !projectId) {
        if (!cancelled) setMessage("");
        return;
      }

      try {
        if (projectId && selectedProject) {
          let count =
            refreshKey > 0 ? null : getProjectSourcesCount(selectedProject);

          if (count == null) {
            const raw = await getFileByProject(projectId);
            count = extractProjectFilesList(raw).length;
          }

          if (cancelled) return;

          if (count === 0) {
            const name = getProjectName(selectedProject).trim() || "this project";
            setMessage(`No knowledge bank is uploaded for this project ${name}.`);
          } else {
            setMessage("");
          }
          return;
        }

        if (clientId) {
          const detail = await getClientById(clientId);
          if (cancelled) return;

          const files = detail?.mediaFiles?.knowledgeBank ?? [];
          if (!files.length) {
            const name =
              getClientDisplayName(detail) ||
              getClientDisplayName(selectedClient) ||
              "this client";
            setMessage(`No knowledge bank is uploaded for this client ${name}.`);
          } else {
            setMessage("");
          }
        }
      } catch {
        if (!cancelled) setMessage("");
      }
    };

    resolveMessage();

    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    projectId,
    refreshKey,
    selectedClient,
    selectedProject,
    selectedProject?.files,
    selectedProject?.sourcesCount,
    selectedProject?.fileCount,
  ]);

  if (!message) return null;

  const handleUploadNow = (event) => {
    event.preventDefault();
    dispatchOpenKbUpload({ projectId: projectId || null });
  };

  return (
    <Box
      sx={{
        mt: 0,
        mb: { xs: 1.5, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        maxWidth: 480,
        mx: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        columnGap: 1.25,
        rowGap: 0.75,
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: 12, sm: 13 },
          fontWeight: 400,
          letterSpacing: "0.01em",
          lineHeight: 1.45,
          color: MESSAGE_COLOR,
          textAlign: "center",
          [`@media ${mobileNavMediaQuery}`]: {
            fontSize: 11.5,
          },
        }}
      >
        {message}
      </Typography>
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={handleUploadNow}
        sx={{
          fontSize: { xs: 12, sm: 13 },
          fontWeight: 500,
          letterSpacing: "0.01em",
          lineHeight: 1.45,
          color: UPLOAD_LINK_COLOR,
          textUnderlineOffset: "3px",
          cursor: "pointer",
          background: "none",
          border: 0,
          p: 0,
          "&:hover": {
            color: "#E0B020",
          },
          [`@media ${mobileNavMediaQuery}`]: {
            fontSize: 11.5,
          },
        }}
      >
        Upload now
      </Link>
    </Box>
  );
}
